// src/components/Gmail/GmailIntegration.tsx
import React, { useEffect, useRef, useState } from "react";
import { Mail, Send, Inbox, CreditCard as Edit, Trash2, Star, Archive, Search, Plus, Paperclip, MoreVertical, Settings, RefreshCw, Filter, CheckCircle, AlertTriangle, X, ShieldAlert, Save, FileText } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";

// ================== GOOGLE OAUTH CONFIG ==================
const GOOGLE_CLIENT_ID =
  "964988759501-5uap86fba359aak5usibjo7taj2tsvt7.apps.googleusercontent.com";

// Gerekli kapsamlar
const GMAIL_SCOPES = [
  "https://www.googleapis.com/auth/gmail.readonly",
  "https://www.googleapis.com/auth/gmail.send",
  "https://www.googleapis.com/auth/gmail.modify",
  "https://www.googleapis.com/auth/gmail.compose", // taslak oluştur/güncelle
  "https://www.googleapis.com/auth/gmail.settings.basic", // sendAs aliaslarını okuyabilmek için
].join(" ");

declare global {
  interface Window {
    google?: any;
  }
}

// ================== Tipler ==================
interface GmailMessage {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  to: string;
  body: string; // plain fallback
  html?: string; // varsa html içerik
  date: Date;
  isRead: boolean;
  isStarred: boolean;
  isImportant: boolean;
  labels: string[];
}

interface GmailThread {
  id: string; // threadId
  subject: string;
  participants: string[];
  messageCount: number;
  lastMessage: GmailMessage;
  isRead: boolean;
  isStarred: boolean;
  labels: string[];
  draftId?: string; // drafts sekmesinde doldurulur
}

interface EmailTemplate {
  id: string;
  name: string;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
}

/* ================== İzole E-posta Görüntüleyici ==================
 - HTML'i uygulamadan izole etmek için <iframe srcDoc> kullanır
 - script çalıştırmaz (sandbox="")
 - sabit yükseklik: düzen bozulmasını önler
 ================================================================= */
const EmailHtmlViewer: React.FC<{ html: string }> = ({ html }) => {
  const baseCss = `
:root { color-scheme: light; }
* { box-sizing: border-box; }
html, body { margin:0; padding:0; }
body {
  font: 14px/1.5 ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji";
  color:#111827; background:transparent; padding:16px;
}
img, video, canvas, svg { max-width:100%; height:auto; }
table { width:100%; border-collapse: collapse; overflow:auto; display:block; }
th, td { border:1px solid #e5e7eb; padding:6px 8px; vertical-align: top; }
a { color:#2563eb; text-decoration: underline; word-break: break-word; }
pre, code { white-space: pre-wrap; word-break: break-word; }
blockquote { margin:0; padding-left:12px; border-left:3px solid #e5e7eb; color:#374151; }
ul,ol { padding-left:20px; }
img { background: #f9fafb; }
`;
  const doc = `<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>${baseCss}</style>
</head>
<body>${html || ""}</body>
</html>`;

  return <iframe sandbox="" srcDoc={doc} className="w-full h-[60vh] border-0" />;
};

// ================== Bileşen ==================
const GmailIntegration: React.FC = () => {
  const { user } = useAuth();

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [gmailSignatures, setGmailSignatures] = useState<any[]>([]);
  const [selectedSignature, setSelectedSignature] = useState<string>("");
  const [loadingSignatures, setLoadingSignatures] = useState(false);

  const [activeTab, setActiveTab] = useState<
    "inbox" | "sent" | "drafts" | "starred" | "trash" | "archived" | "spam" | "signatures"
  >("inbox");

  const [searchTerm, setSearchTerm] = useState("");
  const [showCompose, setShowCompose] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [userSignature, setUserSignature] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [threads, setThreads] = useState<GmailThread[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set()); // messageId seçimi

  // Ayrıntı paneli için:
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selectedThreadMessages, setSelectedThreadMessages] = useState<GmailMessage[]>([]);

  const accessTokenRef = useRef<string | null>(null);
  const tokenClientRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Compose / Taslak durumu
  const [composeData, setComposeData] = useState({
    draftId: null as string | null,
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
    attachments: [] as File[],
    from: "",
  });

  // Gmail sendAs alias'ları
  const [sendAsAliases, setSendAsAliases] = useState<any[]>([]);
  const [loadingAliases, setLoadingAliases] = useState(false);
  const [error, setError] = useState("");

  // --------- yardımcılar ---------
  const loadGisScript = () =>
    new Promise<void>((resolve, reject) => {
      if (window.google?.accounts?.oauth2) return resolve();
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = reject;
      document.head.appendChild(s);
    });

  const b64UrlDecode = (str: string) => {
    const pad = str.length % 4 === 0 ? "" : "=".repeat(4 - (str.length % 4));
    const base64 = str.replace(/-/g, "+").replace(/_/g, "/") + pad;
    try {
      return decodeURIComponent(escape(window.atob(base64)));
    } catch {
      return window.atob(base64);
    }
  };

  const b64UrlEncode = (str: string) => {
    const encoded = btoa(unescape(encodeURIComponent(str)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    return encoded;
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    // MIME partları için standart base64
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  };

  // ---- EK: güvenli header ve base64 sarma yardımcıları ----
  const sanitizeHeaderValue = (s: string) => s.replace(/[\r\n"]/g, (ch) => (ch === `"` ? '\\"' : ""));
  // RFC 5987 filename* kodlaması (UTF-8)
  const encodeRFC5987ValueChars = (str: string) =>
    str
      .replace(/['()*]/g, (c) => "%" + c.charCodeAt(0).toString(16))
      .replace(/%(7C|60|5E)/g, "%$1");
  // Base64 içeriklerini MIME için 76 karakterde satırlara sar
  const wrapBase64 = (b64: string) => b64.replace(/.{1,76}/g, "$&\r\n");
  // Non-ASCII var mı?
  const hasNonAscii = (s: string) => /[^\x00-\x7F]/.test(s);
  // Header (Subject, Display Name) için RFC 2047 (UTF-8, Base64) encode
  const encodeHeaderRFC2047 = (s: string) => {
    if (!s) return "";
    const b64 = btoa(unescape(encodeURIComponent(s)));
    return `=?UTF-8?B?${b64}?=`;
  };
  // UTF-8 string'i base64'a çevir (gövde için)
  const utf8ToBase64 = (s: string) => btoa(unescape(encodeURIComponent(s)));

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (hours < 1) return "Az önce";
    if (hours < 24) return `${hours} saat önce`;
    if (days < 7) return `${days} gün önce`;
    return date.toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
  };

  // HTML gövdeyi çıkar
  const extractHtmlBody = (msg: any): string | undefined => {
    const walk = (p: any): string | undefined => {
      if (!p) return;
      if (p.mimeType === "text/html" && p.body?.data) return b64UrlDecode(p.body.data);
      if (p.parts) {
        for (const c of p.parts) {
          const found = walk(c);
          if (found) return found;
        }
      }
    };
    if (msg.payload?.mimeType === "text/html" && msg.payload?.body?.data) {
      return b64UrlDecode(msg.payload.body.data);
    }
    return walk(msg.payload);
  };

  const extractPlainBody = (msg: any): string => {
    const tryParts = (nodes: any[]): string | null => {
      for (const p of nodes) {
        if (p.mimeType === "text/plain" && p.body?.data) return p.body.data;
        if (p.parts) {
          const res = tryParts(p.parts);
          if (res) return res;
        }
      }
      return null;
    };
    const direct = msg.payload?.body?.data;
    const fromParts = tryParts(msg.payload?.parts || []);
    const data = direct || fromParts || "";
    return data ? b64UrlDecode(data) : "";
  };

  const getHeaders = (payload: any) => (payload?.headers || []) as Array<{ name: string; value: string }>;
  const hval = (headers: Array<{ name: string; value: string }>, name: string) =>
    headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value || "";

  // --- Önizleme metni yardımcıları ---
  const stripHtml = (html: string) => html.replace(/<[^>]+>/g, " ");
  const snippetFrom = (m: GmailMessage, max = 160) => {
    const src = m.html ? stripHtml(m.html) : m.body || "";
    const text = src.replace(/\s+/g, " ").trim();
    return text.length > max ? text.slice(0, max - 1) + "…" : text;
  };

  const loadUserSignature = () => {
    try {
      const savedSignature = localStorage.getItem(`gmail_signature_${user?.id}`);
      if (savedSignature) {
        setUserSignature(savedSignature);
      } else {
        setUserSignature(
          `\n\nSaygılarımla,\n${user?.name || "Danışman"}\nPisa Educazione\n${user?.email || ""}\n${(user as any)?.phone || ""}`
        );
      }
    } catch (error) {
      console.error('Error loading signature:', error);
      setUserSignature(
        `\n\nSaygılarımla,\n${user?.name || "Danışman"}\nPisa Educazione\n${user?.email || ""}`
      );
    }
  };

  const saveUserSignature = () => {
    try {
      localStorage.setItem(`gmail_signature_${user?.id}`, userSignature);
      setShowSignatureModal(false);
      alert("İmza başarıyla kaydedildi!");
    } catch (error) {
      console.error('Error saving signature:', error);
      alert("İmza kaydedilirken bir hata oluştu.");
    }
  };

  // --------- Gmail Token Persistence ---------
  const saveGmailToken = async (accessToken: string, userEmail: string) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('gmail_tokens')
        .upsert({
          user_id: user.id,
          access_token: accessToken,
          user_email: userEmail,
          token_expires_at: new Date(Date.now() + 3600000).toISOString(), // 1 hour from now
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error saving Gmail token:', error);
      }
    } catch (error) {
      console.error('Error saving Gmail token:', error);
    }
  };

  const loadGmailToken = async () => {
    if (!user?.id) return null;

    try {
      const { data, error } = await supabase
        .from('gmail_tokens')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading Gmail token:', error);
        return null;
      }

      if (!data) return null;

      // Check if token is expired
      if (data.token_expires_at && new Date(data.token_expires_at) < new Date()) {
        console.log('Gmail token expired');
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error loading Gmail token:', error);
      return null;
    }
  };

  const deleteGmailToken = async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('gmail_tokens')
        .delete()
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting Gmail token:', error);
      }
    } catch (error) {
      console.error('Error deleting Gmail token:', error);
    }
  };

  const loadSendAsAliases = async (accessToken: string) => {
    try {
      setLoadingAliases(true);
      const response = await fetch("https://gmail.googleapis.com/gmail/v1/users/me/settings/sendAs", {
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error(`Gmail API hatası: ${response.status}`);
      }
      const data = await response.json();
      const aliases = (data.sendAs || []).filter((alias: any) => alias.verificationStatus === "accepted" || alias.isPrimary);
      setSendAsAliases(aliases);
      // Varsayılan göndereni ayarla (primary varsa onu, yoksa ilk alias)
      const def = aliases.find((a: any) => a.isPrimary) || aliases[0];
      if (def) {
        setComposeData((prev) => ({ ...prev, from: def.sendAsEmail }));
      }
    } catch (err) {
      console.error("Send As aliases yüklenirken hata:", err);
      setError("Gönderici hesapları yüklenemedi");
    } finally {
      setLoadingAliases(false);
    }
  };

  const loadGmailSignatures = async () => {
    setLoadingSignatures(true);
    try {
      // Simülasyon (gerçek uygulamada Gmail API kullanılacak)
      await new Promise((resolve) => setTimeout(resolve, 600));
      const emailLeft = (userEmail || "").split("@")[0] || "danisman";
      const logoUrl = "https://i.hizliresim.com/it2lmxn.png";

      const mockSignatures = [
        {
          id: "signature-1",
          name: "Varsayılan İmza",
          content: `${emailLeft}\nPisa Educazione\nİtalya Eğitim Danışmanlığı\nTel: +90 212 555 0000\nEmail: ${userEmail}\n\n<img src="${logoUrl}" alt="Pisa Educazione" style="max-width: 150px; margin-top: 10px;">`,
          isDefault: true,
        },
        {
          id: "signature-2",
          name: "Resmi İmza",
          content: `Saygılarımla,\n\n${emailLeft}\nEğitim Danışmanı\nPisa Educazione\nİtalya Eğitim ve Vize Danışmanlığı\n\nTelefon: +90 212 555 0000\nEmail: ${userEmail}\nWeb: www.pisaeducazione.com\n\n<img src="${logoUrl}" alt="Pisa Educazione" style="max-width: 150px; margin-top: 10px;">`,
          isDefault: false,
        },
        {
          id: "signature-3",
          name: "Kısa İmza",
          content: `${emailLeft} | Pisa Educazione | ${userEmail}\n\n<img src="${logoUrl}" alt="Pisa Educazione" style="max-width: 120px; margin-top: 5px;">`,
          isDefault: false,
        },
      ];
      setGmailSignatures(mockSignatures);
      const defaultSig = mockSignatures.find((sig) => sig.isDefault);
      if (defaultSig) {
        setSelectedSignature(defaultSig.id);
        localStorage.setItem("gmail_selected_signature", defaultSig.id);
      }
    } catch (error) {
      console.error("Gmail imzaları yüklenemedi:", error);
    } finally {
      setLoadingSignatures(false);
    }
  };

  const handleSignatureChange = (signatureId: string) => {
    setSelectedSignature(signatureId);
    localStorage.setItem("gmail_selected_signature", signatureId);
    const signature = gmailSignatures.find((sig) => sig.id === signatureId);
    if (signature) {
      alert(`"${signature.name}" imzası seçildi ve otomatik olarak emaillerinize eklenecek.`);
    }
  };

  const getSelectedSignatureContent = () => {
    const signature = gmailSignatures.find((sig) => sig.id === selectedSignature);
    return signature ? signature.content : "";
    };

  // --------- OAuth init ---------
  useEffect(() => {
    (async () => {
      try {
        await loadGisScript();
        if (!window.google?.accounts?.oauth2) {
          console.error("Google OAuth library not loaded");
          setError("Google OAuth library not loaded");
          return;
        }
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: GMAIL_SCOPES,
          prompt: "consent",
          callback: async (resp: any) => {
            if (resp.error) {
              console.error("OAuth error:", resp);
              setError("Google oturum açma başarısız.");
              return;
            }
            accessTokenRef.current = resp.access_token;
            const emailAddr = user?.email || "me@example.com";
            setIsConnected(true);
            setUserEmail(emailAddr);
            setError("");

            // Save token to database
            await saveGmailToken(resp.access_token, emailAddr);

            // İmzalar
            loadGmailSignatures();
            // İlk veri
            void loadGmailData();
            // Aliaslar
            loadSendAsAliases(resp.access_token);
          },
        });
      } catch (e) {
        console.error("GIS init error:", e);
        setError("Google kimlik hizmeti yüklenemedi.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user signature on mount
  useEffect(() => {
    if (user?.id) {
      loadUserSignature();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Load saved Gmail token on mount
  useEffect(() => {
    const loadSavedToken = async () => {
      if (!user?.id) return;

      console.log('Checking for saved Gmail token...');
      const tokenData = await loadGmailToken();

      if (tokenData && tokenData.access_token) {
        console.log('Saved token found, restoring session...');
        accessTokenRef.current = tokenData.access_token;
        setIsConnected(true);
        setUserEmail(tokenData.user_email);
        setError("");

        // Load Gmail data with saved token
        loadGmailSignatures();
        void loadGmailData();
        loadSendAsAliases(tokenData.access_token);
      } else {
        console.log('No saved token found');
      }
    };

    // Wait a bit for user to be fully loaded
    const timer = setTimeout(() => {
      loadSavedToken();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const connectGmail = () => {
    if (!tokenClientRef.current) return;
    setIsConnecting(true);
    try {
      tokenClientRef.current.requestAccessToken({ prompt: "consent" });
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectGmail = async () => {
    const token = accessTokenRef.current;

    // Delete token from database
    await deleteGmailToken();

    if (token && window.google?.accounts?.oauth2?.revoke) {
      window.google.accounts.oauth2.revoke(token, () => {
        accessTokenRef.current = null;
        setIsConnected(false);
        setThreads([]);
        setSelectedIds(new Set());
        setSelectedThreadId(null);
        setSelectedThreadMessages([]);
        setGmailSignatures([]);
      });
    } else {
      accessTokenRef.current = null;
      setIsConnected(false);
      setThreads([]);
      setSelectedIds(new Set());
      setSelectedThreadId(null);
      setSelectedThreadMessages([]);
      setGmailSignatures([]);
    }
  };

  // --------- Gmail REST yardımcıları ---------
  const gmailFetch = async (url: string, init?: RequestInit) => {
    const token = accessTokenRef.current;
    if (!token) throw new Error("Access token yok");
    const resp = await fetch(url, {
      ...init,
      headers: { ...(init?.headers || {}), Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Gmail API error ${resp.status}: ${text}`);
    }
    return resp.json();
  };

  // --------- Listeleme ---------
  const labelForTab = (tab: typeof activeTab): string | null => {
    switch (tab) {
      case "inbox":
        return "INBOX";
      case "sent":
        return "SENT";
      case "starred":
        return "STARRED";
      case "trash":
        return "TRASH";
      case "spam":
        return "SPAM";
      case "drafts":
        return null; // ayrı uç noktadan
      case "archived":
        return null; // özel q ile
      default:
        return null;
    }
  };

  const loadGmailData = async () => {
    try {
      setIsLoading(true);
      setSelectedIds(new Set());
      setSelectedThreadId(null);
      setSelectedThreadMessages([]);

      if (activeTab === "drafts") {
        // Taslaklar
        const list = await gmailFetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/drafts?maxResults=25${searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ""}`
        );
        const drafts = list.drafts || [];
        const details = await Promise.all(
          drafts.map((d: any) =>
            gmailFetch(`https://gmail.googleapis.com/gmail/v1/users/me/drafts/${d.id}`)
          )
        );
        const parsed = details.map((d: any) => {
          const draftId = d.id;
          const m = d.message;
          const headers = getHeaders(m.payload);
          const subject = hval(headers, "Subject") || "(Konu yok)";
          const from = hval(headers, "From");
          const to = hval(headers, "To");
          const date = new Date(parseInt(m.internalDate, 10));
          const labels: string[] = m.labelIds || [];
          const isRead = !labels.includes("UNREAD");
          const isStarred = labels.includes("STARRED");
          const isImportant = labels.includes("IMPORTANT");
          const gmMsg: GmailMessage = {
            id: m.id,
            threadId: m.threadId,
            subject,
            from,
            to,
            body: extractPlainBody(m),
            html: extractHtmlBody(m),
            date,
            isRead,
            isStarred,
            isImportant,
            labels,
          };
          const th: GmailThread = {
            id: m.threadId,
            subject,
            participants: [from || to || "Taslak"],
            messageCount: 1,
            lastMessage: gmMsg,
            isRead,
            isStarred,
            labels,
            draftId,
          };
          return th;
        });
        setThreads(parsed.sort((a, b) => b.lastMessage.date.getTime() - a.lastMessage.date.getTime()));
        return;
      }

      // Diğer sekmeler
      const label = labelForTab(activeTab);
      const derivedQ =
        activeTab === "archived"
          ? `-in:inbox -in:spam -in:trash${searchTerm ? ` ${searchTerm}` : ""}`
          : searchTerm || "";

      const listUrl =
        `https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=25` +
        (label ? `&labelIds=${label}` : "") +
        (derivedQ ? `&q=${encodeURIComponent(derivedQ)}` : "");

      const list = await gmailFetch(listUrl);
      const ids: string[] = (list.messages || []).map((m: any) => m.id);
      if (ids.length === 0) {
        setThreads([]);
        return;
      }

      const details = await Promise.all(
        ids.map((id) => gmailFetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${id}?format=full`))
      );

      const tmpMap = new Map<string, GmailThread>();
      for (const m of details) {
        const headers = getHeaders(m.payload);
        const subject = hval(headers, "Subject") || "(Konu yok)";
        const from = hval(headers, "From");
        const to = hval(headers, "To");
        const date = new Date(parseInt(m.internalDate, 10));
        const labels: string[] = m.labelIds || [];
        const isRead = !labels.includes("UNREAD");
        const isStarred = labels.includes("STARRED");
        const isImportant = labels.includes("IMPORTANT");
        const gmMsg: GmailMessage = {
          id: m.id,
          threadId: m.threadId,
          subject,
          from,
          to,
          body: extractPlainBody(m),
          html: extractHtmlBody(m),
          date,
          isRead,
          isStarred,
          isImportant,
          labels,
        };
        const existing = tmpMap.get(m.threadId);
        if (!existing || existing.lastMessage.date < date) {
          tmpMap.set(m.threadId, {
            id: m.threadId,
            subject,
            participants: [from || to || ""],
            messageCount: (existing?.messageCount || 0) + 1,
            lastMessage: gmMsg,
            isRead: false,
            isStarred,
            labels,
          });
        } else {
          existing.messageCount += 1;
        }
      }
      const result = Array.from(tmpMap.values()).sort(
        (a, b) => b.lastMessage.date.getTime() - a.lastMessage.date.getTime()
      );
      setThreads(result);
    } catch (e: any) {
      console.error(e);
      alert("Gmail verileri alınamadı: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // sekme değiştiğinde listele
  useEffect(() => {
    if (isConnected) void loadGmailData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isConnected]);

  // --------- Thread ayrıntısı ---------
  const loadThreadDetail = async (threadId: string) => {
    try {
      setIsLoading(true);
      const data = await gmailFetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/threads/${threadId}?format=full`
      );
      const msgs: GmailMessage[] = (data.messages || []).map((m: any) => {
        const headers = getHeaders(m.payload);
        const subject = hval(headers, "Subject") || "(Konu yok)";
        const from = hval(headers, "From");
        const to = hval(headers, "To");
        const date = new Date(parseInt(m.internalDate, 10));
        const labels: string[] = m.labelIds || [];
        const isRead = !labels.includes("UNREAD");
        const isStarred = labels.includes("STARRED");
        const isImportant = labels.includes("IMPORTANT");
        return {
          id: m.id,
          threadId: m.threadId,
          subject,
          from,
          to,
          body: extractPlainBody(m),
          html: extractHtmlBody(m),
          date,
          isRead,
          isStarred,
          isImportant,
          labels,
        };
      });
      setSelectedThreadId(threadId);
      setSelectedThreadMessages(msgs);
    } catch (e: any) {
      console.error(e);
      alert("Mesaj ayrıntısı alınamadı: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // --------- Aksiyonlar (çoklu seçime göre) ---------
  const runModify = async (messageId: string, add: string[] = [], remove: string[] = []) => {
    await gmailFetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}/modify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ addLabelIds: add, removeLabelIds: remove }),
    });
  };

  const onArchive = async () => {
    try {
      setIsLoading(true);
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => runModify(id, [], ["INBOX"])));
      await loadGmailData();
    } catch (e: any) {
      alert("Arşivleme hatası: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onTrash = async () => {
    try {
      setIsLoading(true);
      const ids = Array.from(selectedIds);
      await Promise.all(
        ids.map((id) =>
          gmailFetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${id}/trash`, { method: "POST" })
        )
      );
      await loadGmailData();
    } catch (e: any) {
      alert("Silme/Çöp hatası: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onToggleStar = async () => {
    try {
      setIsLoading(true);
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => runModify(id, ["STARRED"], [])));
      await loadGmailData();
    } catch (e: any) {
      alert("Yıldızlama hatası: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onMarkRead = async () => {
    try {
      setIsLoading(true);
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => runModify(id, [], ["UNREAD"])));
      await loadGmailData();
    } catch (e: any) {
      alert("Okundu işaretleme hatası: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onMarkUnread = async () => {
    try {
      setIsLoading(true);
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => runModify(id, ["UNREAD"], [])));
      await loadGmailData();
    } catch (e: any) {
      alert("Okunmadı işaretleme hatası: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ============ MIME mail inşa ============
  const buildMimeMessage = async () => {
    const boundary = "_____crm_multipart_boundary_" + Math.random().toString(36).slice(2);
    const fromEmail = sanitizeHeaderValue(composeData.from || user?.email || "me");
    const fromAlias = sendAsAliases.find((a) => a.sendAsEmail === composeData.from);
    const displayName = (fromAlias?.displayName || "").trim();

    // From: "Ad Soyad" <mail@...> (non-ASCII varsa RFC 2047)
    let fromHeader: string;
    if (displayName) {
      const namePart = hasNonAscii(displayName)
        ? encodeHeaderRFC2047(displayName) // encoded-word, tırnaksız
        : `"${sanitizeHeaderValue(displayName)}"`; // ASCII ise quoted
      fromHeader = `${namePart} <${fromEmail}>`;
    } else {
      fromHeader = fromEmail;
    }

    // To/CC/BCC
    const toHdr = sanitizeHeaderValue(composeData.to || "");
    const ccHdr = sanitizeHeaderValue(composeData.cc || "");
    const bccHdr = sanitizeHeaderValue(composeData.bcc || "");

    // Subject: Non-ASCII varsa RFC 2047
    const rawSubject = composeData.subject || "";
    const subjectHdr = hasNonAscii(rawSubject) ? encodeHeaderRFC2047(rawSubject) : sanitizeHeaderValue(rawSubject);

    let mimeMsg = "";
    mimeMsg += `From: ${fromHeader}\r\n`;
    mimeMsg += `To: ${toHdr}\r\n`;
    if (ccHdr) mimeMsg += `Cc: ${ccHdr}\r\n`;
    if (bccHdr) mimeMsg += `Bcc: ${bccHdr}\r\n`;
    mimeMsg += `Subject: ${subjectHdr}\r\n`;
    mimeMsg += `MIME-Version: 1.0\r\n`;

    // İmzayı ekle
    const emailWithSignature = (composeData.body || "") + (userSignature || "");
    const bodyB64Wrapped = wrapBase64(utf8ToBase64(emailWithSignature || ""));

    if (composeData.attachments.length > 0) {
      // Çok parçalı: gövde + ekler
      mimeMsg += `Content-Type: multipart/mixed; boundary="${boundary}"\r\n\r\n`;
      // Text part
      mimeMsg += `--${boundary}\r\n`;
      mimeMsg += `Content-Type: text/plain; charset="UTF-8"\r\n`;
      mimeMsg += `Content-Transfer-Encoding: base64\r\n\r\n`;
      mimeMsg += `${bodyB64Wrapped}\r\n`;

      // Ekler
      for (const file of composeData.attachments) {
        const buffer = await file.arrayBuffer();
        const base64 = arrayBufferToBase64(buffer);
        const base64Wrapped = wrapBase64(base64);
        const mimeType = file.type || "application/octet-stream";
        const safeName = sanitizeHeaderValue(file.name);
        const rfcName = encodeRFC5987ValueChars(file.name);

        mimeMsg += `--${boundary}\r\n`;
        mimeMsg += `Content-Type: ${mimeType}; name="${safeName}"\r\n`;
        mimeMsg += `Content-Disposition: attachment; filename="${safeName}"; filename*=UTF-8''${rfcName}\r\n`;
        mimeMsg += `Content-Transfer-Encoding: base64\r\n\r\n`;
        mimeMsg += `${base64Wrapped}\r\n`;
      }

      // Kapanış
      mimeMsg += `--${boundary}--\r\n`;
    } else {
      // Sadece metin
      mimeMsg += `Content-Type: text/plain; charset="UTF-8"\r\n`;
      mimeMsg += `Content-Transfer-Encoding: base64\r\n\r\n`;
      mimeMsg += `${bodyB64Wrapped}\r\n`;
    }

    // Gmail raw için URL-safe base64
    return b64UrlEncode(mimeMsg);
  };

  // --------- Gönder / Taslak ---------
  const handleSendEmail = async () => {
    if (!composeData.from) {
      alert("Lütfen gönderen e-posta adresini seçin.");
      return;
    }
    // Seçilen from adresi doğrulanmış mı?
    const allowed = sendAsAliases.some((a) => a.sendAsEmail === composeData.from);
    if (!allowed) {
      alert(
        'Seçilen gönderen adresi Gmail hesabınızda doğrulanmış değil. Gmail > Ayarlar > Hesaplar > "Postayı şu olarak gönder" bölümünden ekleyip doğrulayın.'
      );
      return;
    }

    try {
      setIsLoading(true);
      const encoded = await buildMimeMessage();

      if (composeData.draftId) {
        // Taslaktan gönder
        await gmailFetch("https://gmail.googleapis.com/gmail/v1/users/me/drafts/send", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: composeData.draftId, message: { raw: encoded } }),
        });
      } else {
        // Normal gönder
        await gmailFetch("https://gmail.googleapis.com/gmail/v1/users/me/messages/send", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ raw: encoded }),
        });
      }

      alert("Email gönderildi.");
      setShowCompose(false);
      const defFrom =
        sendAsAliases.find((a) => a.isPrimary)?.sendAsEmail ||
        sendAsAliases[0]?.sendAsEmail ||
        "";
      setComposeData({
        draftId: null,
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        body: "",
        attachments: [],
        from: defFrom,
      });
      void loadGmailData();
    } catch (e: any) {
      console.error(e);
      alert("Email gönderilemedi: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsLoading(true);
      const encoded = await buildMimeMessage();

      if (composeData.draftId) {
        // update draft
        await gmailFetch(`https://gmail.googleapis.com/gmail/v1/users/me/drafts/${composeData.draftId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: { raw: encoded } }),
        });
      } else {
        // yeni draft
        const res = await gmailFetch("https://gmail.googleapis.com/gmail/v1/users/me/drafts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: { raw: encoded } }),
        });
        setComposeData((prev) => ({ ...prev, draftId: res.id }));
      }

      alert("Taslak kaydedildi.");
      setShowCompose(false);
      const defFrom =
        sendAsAliases.find((a) => a.isPrimary)?.sendAsEmail ||
        sendAsAliases[0]?.sendAsEmail ||
        "";
      setComposeData({
        draftId: null,
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        body: "",
        attachments: [],
        from: defFrom,
      });
      if (activeTab === "drafts") void loadGmailData();
    } catch (e: any) {
      console.error(e);
      alert("Taslak kaydedilemedi: " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Taslak aç (listede taslağa tıklanınca)
  const openDraftFromThread = (thread: GmailThread) => {
    const last = thread.lastMessage;
    setComposeData({
      draftId: thread.draftId || null,
      to: last.to || "",
      cc: "",
      bcc: "",
      subject: thread.subject || "",
      body: last.body || "",
      attachments: [],
      from: composeData.from,
    });
    setShowCompose(true);
  };

  const handleComposeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setComposeData((prev) => ({ ...prev, [name]: value }));
  };

  // ======= ŞABLONLAR - Storage =======
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templateForm, setTemplateForm] = useState<EmailTemplate>({
    id: "",
    name: "",
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
  });

  const persistTemplates = (next: EmailTemplate[]) => {
    setTemplates(next);
    try {
      localStorage.setItem("gmail_templates", JSON.stringify(next));
    } catch {}
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem("gmail_templates");
      if (raw) {
        setTemplates(JSON.parse(raw));
      } else {
        const seed: EmailTemplate[] = [
          {
            id: "seed-1",
            name: "Toplantı Daveti",
            to: "",
            cc: "",
            bcc: "",
            subject: "Toplantı Daveti - {Şirket/Proje}",
            body:
              "Merhaba,\n\n{konu} hakkında kısa bir toplantı planlamak isterim. Müsait olduğunuz zaman dilimlerini paylaşabilir misiniz?\n\nSaygılarımla,\n",
          },
          {
            id: "seed-2",
            name: "Satış Teklifi",
            to: "",
            cc: "",
            bcc: "",
            subject: "{Ürün/Hizmet} Teklifi",
            body:
              "Merhaba,\n\nİstediğiniz {ürün/hizmet} için teklifimizi ekte paylaşıyorum. Sorularınız olursa memnuniyetle yardımcı olurum.\n\nİyi çalışmalar,\n",
          },
        ];
        persistTemplates(seed);
      }
    } catch {}
  }, []);

  // Şablon uygula
  const applyTemplate = (tpl: EmailTemplate) => {
    setComposeData((prev) => ({
      ...prev,
      draftId: null, // şablon uygularken yeni mail
      to: tpl.to || prev.to,
      cc: tpl.cc || prev.cc,
      bcc: tpl.bcc || prev.bcc,
      subject: tpl.subject || prev.subject,
      body: tpl.body || prev.body,
    }));
    if (!showCompose) setShowCompose(true);
  };

  // Şablon Kaydet/Güncelle
  const saveTemplate = () => {
    const trimmedName = templateForm.name.trim();
    if (!trimmedName) {
      alert("Şablon adı gerekli.");
      return;
    }
    if (templateForm.id) {
      const next = templates.map((t) => (t.id === templateForm.id ? { ...templateForm, name: trimmedName } : t));
      persistTemplates(next);
    } else {
      const newTpl: EmailTemplate = { ...templateForm, id: "tpl-" + Date.now() };
      persistTemplates([newTpl, ...templates]);
    }
    setTemplateForm({ id: "", name: "", to: "", cc: "", bcc: "", subject: "", body: "" });
  };

  const editTemplate = (tpl: EmailTemplate) => {
    setTemplateForm({ ...tpl });
  };

  const deleteTemplate = async (id: string) => {
    if (!await window.appConfirm("Bu şablonu silmek istiyor musunuz?")) return;
    persistTemplates(templates.filter((t) => t.id !== id));
    if (templateForm.id === id) {
      setTemplateForm({ id: "", name: "", to: "", cc: "", bcc: "", subject: "", body: "" });
    }
  };

  // --------- UI ---------
  if (!isConnected) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Gmail Entegrasyonu</h2>
          <p className="text-gray-600 dark:text-gray-400">Gmail hesabınızı bağlayarak email'lerinizi CRM'den yönetin</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <p className="text-red-800 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Gmail Hesabınızı Bağlayın</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Gmail hesabınızı bağlayarak email'lerinizi CRM sistemi içinden yönetebilir, müşterilerinizle daha etkili
            iletişim kurabilirsiniz.
          </p>

          <div className="space-y-4 mb-6">
            <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Email'leri CRM içinden okuma ve gönderme</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Müşteri email geçmişini otomatik takip</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Email şablonları ve otomatik yanıtlar</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Lead'lerle email etkileşimini otomatik kaydetme</span>
            </div>
          </div>

          <button
            onClick={connectGmail}
            disabled={isLoading || isConnecting}
            className={`flex items-center space-x-2 mx-auto px-6 py-3 rounded-lg font-medium transition-all ${
              isLoading || isConnecting
                ? "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500"
                : "bg-red-600 hover:bg-red-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-red-600/25"
            }`}
          >
            <Mail className={`w-5 h-5 ${isLoading || isConnecting ? "animate-pulse" : ""}`} />
            <span>{isLoading || isConnecting ? "Bağlanıyor..." : "Gmail'i Bağla"}</span>
          </button>

          <p className="text-xs text-gray-500 dark:text-gray-500 mt-4">
            Google hesabınızla güvenli OAuth2 popup akışı başlatılacak
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Gmail</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Bağlı hesap: {user?.email || "me"} • {threads.filter((t) => !t.isRead).length} okunmamış
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => loadGmailData()}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Yenile</span>
          </button>

          {/* ŞABLONLAR */}
          <button
            onClick={() => setShowTemplatesModal(true)}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span>Şablonlar</span>
          </button>

          <button
            onClick={() => setShowSignatureModal(true)}
            className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span>İmza Ayarları</span>
          </button>

          <button
            onClick={() => {
              const def =
                sendAsAliases.find((a) => a.isPrimary)?.sendAsEmail ||
                sendAsAliases[0]?.sendAsEmail ||
                composeData.from ||
                "";
              setComposeData({
                draftId: null,
                to: "",
                cc: "",
                bcc: "",
                subject: "",
                body: "",
                attachments: [],
                from: def,
              });
              setShowCompose(true);
            }}
            className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Yeni Email</span>
          </button>

          <button
            onClick={disconnectGmail}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
            <span>Bağlantıyı Kes</span>
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      {isConnected && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              const signatureContent = getSelectedSignatureContent();
              const subject = encodeURIComponent("Pisa Educazione - Eğitim Danışmanlığı");
              const body = encodeURIComponent(`Merhaba,\n\nPisa Educazione'den size ulaşıyorum.\n\n${signatureContent}`);
              window.open(`https://mail.google.com/mail/?view=cm&su=${subject}&body=${body}`, "_blank");
            }}
            disabled={isLoading}
            className="flex items-center space-x-3 p-4 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30 rounded-xl transition-colors disabled:opacity-50"
          >
            <Mail className="w-6 h-6 text-green-600 dark:text-green-400" />
            <div className="text-left">
              <p className="font-medium text-green-900 dark:text-green-300">İmzalı Email Yaz</p>
              <p className="text-sm text-green-700 dark:text-green-400">Seçili imza ile email</p>
            </div>
          </button>
        </div>
      )}


      {/* Ana Alan */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden min-h-[600px] h-[calc(100vh-300px)] md:h-[calc(100dvh-300px)]">
        <div className="flex h-full">
          {/* Sidebar */}
          <div className="w-64 border-r border-gray-200 dark:border-gray-700 flex flex-col">
            {/* Search */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Email ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void loadGmailData();
                  }}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 p-2 space-y-1">
              {[
                { id: "inbox", label: "Gelen Kutusu", icon: Inbox },
                { id: "sent", label: "Gönderilen", icon: Send },
                { id: "drafts", label: "Taslaklar", icon: Edit },
                { id: "starred", label: "Yıldızlı", icon: Star },
                { id: "archived", label: "Arşivlenenler", icon: Archive },
                { id: "spam", label: "Spam", icon: ShieldAlert },
                { id: "trash", label: "Çöp Kutusu", icon: Trash2 },
                { id: "signatures", label: "Gmail İmzalarım", icon: FileText },
              ].map((tab) => {
                const Icon = tab.icon as any;
                const isActive = activeTab === (tab.id as any);
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors ${
                      isActive
                        ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                        : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{tab.label}</span>
                    </div>
                  </button>
                );
              })}
            </nav>

            <div className="p-2 border-t border-gray-200 dark:border-gray-700">
              <button className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors">
                <Settings className="w-5 h-5" />
                <span className="font-medium">Ayarlar</span>
              </button>
            </div>
          </div>

          {/* Liste + Ayrıntı */}
          <div className="flex-1 flex h-full min-w-0">
            {activeTab === "signatures" ? (
              /* Signatures Page */
              <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-6xl mx-auto">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <Edit className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gmail İmzalarım</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Email imzalarınızı yönetin</p>
                      </div>
                    </div>
                    <button
                      onClick={loadGmailSignatures}
                      disabled={loadingSignatures}
                      className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-lg text-sm disabled:opacity-50 transition-colors"
                    >
                      <RefreshCw className={`w-4 h-4 ${loadingSignatures ? "animate-spin" : ""}`} />
                      <span>Yenile</span>
                    </button>
                  </div>

                  {loadingSignatures ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3 text-gray-600 dark:text-gray-400">İmzalar yükleniyor...</span>
                    </div>
                  ) : gmailSignatures.length === 0 ? (
                    <div className="text-center py-8">
                      <Mail className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-600 dark:text-gray-400">Gmail hesabınızda kayıtlı imza bulunamadı</p>
                      <button
                        onClick={() => window.open("https://mail.google.com/mail/u/0/#settings/general", "_blank")}
                        className="mt-3 text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Gmail'de İmza Oluştur →
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {gmailSignatures.map((signature) => (
                        <div
                          key={signature.id}
                          className={`group p-5 border-2 rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                            selectedSignature === signature.id
                              ? "border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-md"
                              : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 bg-white dark:bg-gray-800"
                          }`}
                          onClick={() => handleSignatureChange(signature.id)}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <div className={`w-2 h-2 rounded-full ${selectedSignature === signature.id ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                              <h4 className="font-semibold text-gray-900 dark:text-white">{signature.name}</h4>
                            </div>
                            <div className="flex items-center space-x-2">
                              {signature.isDefault && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-medium rounded-full">
                                  Varsayılan
                                </span>
                              )}
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                selectedSignature === signature.id
                                  ? 'border-blue-500 bg-blue-500'
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}>
                                {selectedSignature === signature.id && (
                                  <CheckCircle className="w-3 h-3 text-white" />
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div
                              className="whitespace-pre-wrap text-gray-700 dark:text-gray-300 text-xs line-clamp-6"
                              dangerouslySetInnerHTML={{ __html: signature.content.replace(/\n/g, '<br>') }}
                            />
                          </div>

                          {selectedSignature === signature.id && (
                            <div className="mt-3 flex items-center justify-center">
                              <span className="text-xs font-medium text-blue-600 dark:text-blue-400 flex items-center space-x-1">
                                <CheckCircle className="w-3 h-3" />
                                <span>Aktif</span>
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedSignature && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <span className="font-semibold text-blue-900 dark:text-blue-300">Aktif İmza</span>
                      </div>
                      <p className="text-sm text-blue-800 dark:text-blue-400">
                        Seçili imza otomatik olarak tüm emaillerinize eklenecektir. Lead detay sayfasından email gönderirken de
                        bu imza kullanılacak.
                      </p>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Hızlı İşlemler</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <button
                        onClick={() => setShowSignatureModal(true)}
                        className="flex items-center justify-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Kişisel İmza Düzenle</span>
                      </button>
                      <button
                        onClick={() => window.open("https://mail.google.com/mail/u/0/#settings/general", "_blank")}
                        className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-3 rounded-lg transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Gmail'de Düzenle</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
            {/* Sol: Liste + Toolbar */}
            <div className="flex-1 lg:w-1/2 flex flex-col border-r border-gray-200 dark:border-gray-800 min-w-0">
              {/* Toolbar */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={onArchive}
                      disabled={selectedIds.size === 0 || isLoading}
                      className="p-2 disabled:opacity-50 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                    <button
                      onClick={onTrash}
                      disabled={selectedIds.size === 0 || isLoading}
                      className="p-2 disabled:opacity-50 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={onToggleStar}
                      disabled={selectedIds.size === 0 || isLoading}
                      className="p-2 disabled:opacity-50 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                    <button
                      onClick={onMarkRead}
                      disabled={selectedIds.size === 0 || isLoading}
                      className="px-2 py-1 disabled:opacity-50 text-xs rounded bg-gray-200 dark:bg-gray-700"
                    >
                      Okundu
                    </button>
                    <button
                      onClick={onMarkUnread}
                      disabled={selectedIds.size === 0 || isLoading}
                      className="px-2 py-1 disabled:opacity-50 text-xs rounded bg-gray-200 dark:bg-gray-700"
                    >
                      Okunmadı
                    </button>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                      <Filter className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Liste */}
              <div className="flex-1 overflow-y-auto">
                {threads.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Mail className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        {activeTab === "inbox" ? "Gelen kutunuz boş" : "Henüz email yok"}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {activeTab === "inbox"
                          ? "Yeni email'ler burada görünecek"
                          : "Bu kategoride email bulunmuyor"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {threads
                      .filter(
                        (t) =>
                          !searchTerm ||
                          t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.participants.join(",").toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((thread) => {
                        const checked = selectedIds.has(thread.lastMessage.id);
                        const isSelected = selectedThreadId === thread.id;
                        const onRowClick = () => {
                          if (activeTab === "drafts") {
                            openDraftFromThread(thread);
                          } else {
                            setSelectedThreadId(thread.id);
                            void loadThreadDetail(thread.id);
                          }
                        };
                        return (
                          <div
                            key={thread.id + thread.lastMessage.id}
                            onClick={onRowClick}
                            className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors ${
                              isSelected ? "bg-blue-50 dark:bg-blue-900/20" : ""
                            } ${!thread.isRead ? "bg-blue-25 dark:bg-blue-950/10" : ""}`}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setSelectedIds((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(thread.lastMessage.id);
                                      else next.delete(thread.lastMessage.id);
                                      return next;
                                    });
                                  }}
                                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <button
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                                >
                                  <Star
                                    className={`w-4 h-4 ${
                                      thread.isStarred ? "text-yellow-500 fill-current" : "text-gray-400"
                                    }`}
                                  />
                                </button>
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p
                                    className={`font-medium truncate ${
                                      !thread.isRead ? "text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400"
                                    }`}
                                  >
                                    {thread.participants[0]}
                                  </p>
                                  <span className="text-xs text-gray-500 dark:text-gray-500">
                                    {formatDate(thread.lastMessage.date)}
                                  </span>
                                </div>

                                <p
                                  className={`truncate mt-1 ${
                                    !thread.isRead ? "font-medium text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400"
                                  }`}
                                >
                                  {thread.subject}
                                </p>

                                <p className="text-sm text-gray-500 dark:text-gray-500 truncate mt-1">
                                  {snippetFrom(thread.lastMessage)}
                                </p>

                                <div className="flex items-center space-x-2 mt-2">
                                  {thread.messageCount > 1 && (
                                    <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full">
                                      {thread.messageCount}
                                    </span>
                                  )}
                                  {thread.lastMessage.isImportant && <AlertTriangle className="w-3 h-3 text-yellow-500" />}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            {/* Sağ: Ayrıntı paneli */}
            <div className="flex-1 min-w-0">
              {selectedThreadId && activeTab !== "drafts" ? (
                <div className="h-full flex flex-col">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white/60 dark:bg-gray-900/60 backdrop-blur">
                    <div className="font-semibold text-gray-900 dark:text-white truncate">
                      {selectedThreadMessages[selectedThreadMessages.length - 1]?.subject || "Konu"}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedThreadId(null);
                        setSelectedThreadMessages([]);
                      }}
                      className="px-3 py-1.5 text-sm rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      Kapat
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-6">
                    {selectedThreadMessages.map((m) => (
                      <div key={m.id} className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 dark:text-white truncate">{m.from}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">Kime: {m.to || "—"}</div>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{m.date.toLocaleString("tr-TR")}</div>
                          </div>
                        </div>

                        <div className="bg-white dark:bg-gray-900">
                          {m.html ? (
                            <EmailHtmlViewer html={m.html} />
                          ) : (
                            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 p-4 max-h-[60vh] overflow-y-auto">
                              {m.body || "(İçerik yok)"}
                            </pre>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  {activeTab === "drafts" ? "Bir taslak seçip düzenleyin" : "Bir email seçin"}
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {composeData.draftId ? "Taslak Düzenle" : "Yeni Email"}
              </h3>

              {/* Compose içi Şablon uygula */}
              {templates.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 dark:text-gray-300">Şablon uygula:</label>
                  <select
                    className="text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-2 py-1"
                    defaultValue=""
                    onChange={(e) => {
                      const id = e.target.value;
                      const tpl = templates.find((t) => t.id === id);
                      if (tpl) applyTemplate(tpl);
                      e.currentTarget.value = "";
                    }}
                  >
                    <option value="" disabled>
                      Seçin…
                    </option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                onClick={() => setShowCompose(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Sender Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gönderen Hesap <span className="text-red-500">*</span>
                </label>

                {loadingAliases ? (
                  <div className="flex items-center space-x-2 px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600 dark:text-gray-400">Gönderici hesapları yükleniyor...</span>
                  </div>
                ) : (
                  <select
                    value={composeData.from}
                    onChange={(e) => setComposeData((prev) => ({ ...prev, from: e.target.value }))}
                    required
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Gönderen hesap seçin</option>
                    {sendAsAliases.map((alias) => (
                      <option key={alias.sendAsEmail} value={alias.sendAsEmail}>
                        {alias.displayName ? `${alias.displayName} <${alias.sendAsEmail}>` : alias.sendAsEmail}
                        {alias.isPrimary ? " (Ana Hesap)" : ""}
                      </option>
                    ))}
                  </select>
                )}

                {sendAsAliases.length === 0 && !loadingAliases && (
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                    ⚠️ Doğrulanmış gönderici adres bulunamadı. Gmail → Ayarlar → Hesaplar → "Postayı şu olarak gönder"
                    kısmından alias ekleyip doğrulayın.
                  </p>
                )}
                {error && <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Alıcı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="to"
                  value={composeData.to}
                  onChange={handleComposeChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="email@example.com, başka@ornek.com"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">CC</label>
                  <input
                    type="text"
                    name="cc"
                    value={composeData.cc}
                    onChange={handleComposeChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="cc@example.com, user@site.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">BCC</label>
                  <input
                    type="text"
                    name="bcc"
                    value={composeData.bcc}
                    onChange={handleComposeChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="bcc@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Konu <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={composeData.subject}
                  onChange={handleComposeChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Email konusu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mesaj <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="body"
                  value={composeData.body}
                  onChange={handleComposeChange}
                  rows={12}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  placeholder={`Email içeriğinizi yazın...\n\nNot: İmzanız otomatik olarak eklenecektir.`}
                />

                {/* Signature Preview */}
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">İmza Önizleme:</p>
                  <div
                    className="text-sm text-blue-800 dark:text-blue-400"
                    dangerouslySetInnerHTML={{
                      __html: (getSelectedSignatureContent() || userSignature || 'İmza yok').replace(/\n/g, '<br>')
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignatureModal(true)}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline"
                  >
                    İmzayı düzenle
                  </button>
                </div>
              </div>

              {/* Dosya Ekle */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    id="fileInput"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      if (files.length === 0) return;
                      setComposeData((prev) => {
                        const next = [...prev.attachments, ...files];
                        const totalBytes = next.reduce((sum, f) => sum + f.size, 0);
                        // Gmail pratik limit: ~25MB (base64 şişmesi ~%33). Güvenli taraf: 22MB.
                        if (totalBytes > 22 * 1024 * 1024) {
                          alert("Toplam ek boyutu 22 MB'ı geçemez. Lütfen daha az/büyük olmayan dosyalar seçin.");
                          return prev;
                        }
                        return { ...prev, attachments: next };
                      });
                      // Aynı dosyayı tekrar seçebilmek için input'u temizle
                      e.currentTarget.value = "";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:bg-opacity-80 px-2 py-1 rounded"
                  >
                    <Paperclip className="w-4 h-4" />
                    <span>Dosya Ekle</span>
                  </button>

                  {composeData.attachments.length > 0 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {composeData.attachments.length} dosya eklendi
                    </span>
                  )}
                </div>

                {composeData.attachments.length > 0 && (
                  <ul className="text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                    {composeData.attachments.map((f, idx) => (
                      <li key={`${f.name}-${idx}`} className="flex items-center justify-between px-3 py-2">
                        <div className="truncate">
                          <span className="font-medium">{f.name}</span>
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            {(f.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <button
                          className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                          onClick={() => {
                            setComposeData((prev) => {
                              const next = [...prev.attachments];
                              next.splice(idx, 1);
                              return { ...prev, attachments: next };
                            });
                          }}
                        >
                          Kaldır
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSaveDraft}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 text-sm"
                >
                  Taslak Kaydet
                </button>
                {composeData.draftId && (
                  <span className="text-xs text-gray-400">Taslak ID: {composeData.draftId}</span>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {composeData.from && (
                    <span>
                      Gönderen:{" "}
                      <strong>
                        {sendAsAliases.find((a) => a.sendAsEmail === composeData.from)?.displayName || composeData.from}
                      </strong>{" "}
                      ({composeData.from})
                    </span>
                  )}
                </div>

                <button
                  onClick={() => setShowCompose(false)}
                  className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
                >
                  İptal
                </button>

                <button
                  onClick={handleSendEmail}
                  disabled={isLoading || !composeData.to || !composeData.subject || !composeData.body}
                  className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${
                    isLoading || !composeData.to || !composeData.subject || !composeData.body
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500"
                      : "bg-red-600 hover:bg-red-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-red-600/25"
                  }`}
                >
                  <Send className={`w-4 h-4 ${isLoading ? "animate-pulse" : ""}`} />
                  <span>{isLoading ? "Gönderiliyor..." : composeData.draftId ? "Taslaktan Gönder" : "Gönder"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ŞABLON YÖNETİCİSİ Modal */}
      {showTemplatesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Şablonlar</h3>
              <button
                onClick={() => setShowTemplatesModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sol: Liste */}
              <div>
                <div className="mb-3 text-sm text-gray-600 dark:text-gray-300">
                  Kayıtlı şablonlarınızı yönetin, düzenleyin ve bir tıkla Compose'a uygulayın.
                </div>

                {templates.length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Henüz şablon yok. Sağdaki formdan yeni şablon oluşturun.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {templates.map((t) => (
                      <div key={t.id} className="border border-gray-200 dark:border-gray-800 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-gray-900 dark:text-white truncate">{t.name}</div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                applyTemplate(t);
                                setShowTemplatesModal(false);
                              }}
                              className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                            >
                              Uygula
                            </button>
                            <button
                              onClick={() => editTemplate(t)}
                              className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                            >
                              Düzenle
                            </button>
                            <button
                              onClick={() => deleteTemplate(t.id)}
                              className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                            >
                              Sil
                            </button>
                          </div>
                        </div>

                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                          <span className="font-medium">Konu:</span> {t.subject || "—"}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                          <span className="font-medium">Önizleme:</span>{" "}
                          {(t.body || "").slice(0, 120)}
                          {(t.body || "").length > 120 ? "…" : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sağ: Form */}
              <div className="border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {templateForm.id ? "Şablonu Düzenle" : "Yeni Şablon Oluştur"}
                  </div>
                  {templateForm.id && (
                    <button
                      onClick={() =>
                        setTemplateForm({ id: "", name: "", to: "", cc: "", bcc: "", subject: "", body: "" })
                      }
                      className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      Yeni
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Şablon Adı *</label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white"
                      placeholder="Örn: Toplantı Daveti"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
                      <input
                        type="text"
                        value={templateForm.to}
                        onChange={(e) => setTemplateForm({ ...templateForm, to: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CC</label>
                      <input
                        type="text"
                        value={templateForm.cc}
                        onChange={(e) => setTemplateForm({ ...templateForm, cc: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white"
                        placeholder="cc@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">BCC</label>
                      <input
                        type="text"
                        value={templateForm.bcc}
                        onChange={(e) => setTemplateForm({ ...templateForm, bcc: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white"
                        placeholder="bcc@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Konu</label>
                    <input
                      type="text"
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white"
                      placeholder="Email konusu"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gövde</label>
                    <textarea
                      value={templateForm.body}
                      onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                      rows={8}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white"
                      placeholder="Email içeriği"
                    />
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                      onClick={() =>
                        setTemplateForm({ id: "", name: "", to: "", cc: "", bcc: "", subject: "", body: "" })
                      }
                      className="px-4 py-2 rounded bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      Temizle
                    </button>
                    <button
                      onClick={saveTemplate}
                      className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                    >
                      {templateForm.id ? "Güncelle" : "Oluştur"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-800 text-right">
              <button
                onClick={() => setShowTemplatesModal(false)}
                className="px-4 py-2 rounded bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Electronic Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Elektronik İmza Ayarları</h2>
              </div>
              <button
                onClick={() => setShowSignatureModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 dark:text-blue-300">İmza Bilgisi</h4>
                    <p className="text-sm text-blue-800 dark:text-blue-400 mt-1">
                      Bu imza, gönderdiğiniz tüm emaillerin sonuna otomatik olarak eklenecektir. İmzanızda adınız,
                      pozisyonunuz, iletişim bilgileriniz ve şirket bilgileri yer alabilir.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email İmzanız</label>
                <textarea
                  value={userSignature}
                  onChange={(e) => setUserSignature(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono"
                  placeholder={`Örnek imza:
Saygılarımla,
${user?.name || "Adınız Soyadınız"}
Eğitim Danışmanı
Pisa Educazione

Email: ${user?.email || "email@pisaeducazione.com"}
Telefon: ${(user as any)?.phone || "+90 212 555 0000"}
Web: www.pisaeducazione.com

Bu email gizli ve özel bilgiler içerebilir.`}
                />
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                  İpucu: <code>\n</code> kullanarak satır atlayabilirsiniz. İmzanız her email gönderiminde otomatik eklenir.
                </p>
              </div>

              {/* Signature Templates */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Hazır İmza Şablonları</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setUserSignature(
                        `\n\nSaygılarımla,\n${user?.name || "Adınız Soyadınız"}\nEğitim Danışmanı\nPisa Educazione\n\nEmail: ${
                          user?.email || "email@pisaeducazione.com"
                        }\nTelefon: ${(user as any)?.phone || "+90 212 555 0000"}\nWeb: www.pisaeducazione.com`
                      )
                    }
                    className="p-3 text-left bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors"
                  >
                    <p className="font-medium text-gray-900 dark:text-white text-sm">Standart İmza</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Ad, pozisyon ve iletişim bilgileri</p>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setUserSignature(
                        `\n\nSaygılarımla,\n${user?.name || "Adınız Soyadınız"}\n${
                          (user as any)?.unitType === "education_consultant"
                            ? "Eğitim Danışmanı"
                            : (user as any)?.unitType === "visa_consultant"
                            ? "Vize Danışmanı"
                            : (user as any)?.unitType === "sales"
                            ? "Satış Uzmanı"
                            : "Danışman"
                        }\nPisa Educazione\n\n📧 ${user?.email || "email@pisaeducazione.com"}\n📞 ${
                          (user as any)?.phone || "+90 212 555 0000"
                        }\n🌐 www.pisaeducazione.com\n\n🇮🇹 İtalya'da eğitim hayalinizi gerçekleştirin!\n\nBu email gizli ve özel bilgiler içerebilir. Yanlış alıcıysanız lütfen göndereni bilgilendirin.`
                      )
                    }
                    className="p-3 text-left bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors"
                  >
                    <p className="font-medium text-gray-900 dark:text-white text-sm">Detaylı İmza</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Emoji ve gizlilik notu ile</p>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setUserSignature(
                        `\n\nBest regards,\n${user?.name || "Your Name"}\nEducation Consultant\nPisa Educazione\n\nEmail: ${
                          user?.email || "email@pisaeducazione.com"
                        }\nPhone: ${(user as any)?.phone || "+90 212 555 0000"}\nWebsite: www.pisaeducazione.com`
                      )
                    }
                    className="p-3 text-left bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors"
                  >
                    <p className="font-medium text-gray-900 dark:text-white text-sm">İngilizce İmza</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Uluslararası müşteriler için</p>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setUserSignature(
                        `\n\nCordiali saluti,\n${user?.name || "Il tuo nome"}\nConsulente Educativo\nPisa Educazione\n\nEmail: ${
                          user?.email || "email@pisaeducazione.com"
                        }\nTelefono: ${(user as any)?.phone || "+90 212 555 0000"}\nSito web: www.pisaeducazione.com`
                      )
                    }
                    className="p-3 text-left bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors"
                  >
                    <p className="font-medium text-gray-900 dark:text-white text-sm">İtalyanca İmza</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">İtalyan müşteriler için</p>
                  </button>
                </div>
              </div>

              {/* Signature Preview */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">İmza Önizleme</h4>
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line font-mono">
                    {userSignature || "İmza metni buraya gelecek..."}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={() => setShowSignatureModal(false)}
                className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={saveUserSignature}
                className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>İmzayı Kaydet</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GmailIntegration;
